import { BackgroundsModal } from '@/components/Modals/BackgroundGallery';
import { createImagesBucket, createImagesDns } from '@/environment';
import { useApplyBackground } from '@/hooks/useApplyBackground';
import { useApplyBackgroundColor } from '@/hooks/useApplyBackgroundColor';
import useBackgroundRemoval from '@/hooks/useRemoveBackground';
import { AddAvatarCard } from '@/pages/agents/components/avatars/AddAvatarCard';
import { AvatarTabs } from '@/pages/agents/components/avatars/AvatarTabs';
import DeleteAvatarModal from '@/pages/agents/components/avatars/DeleteAvatarModal';
import { PresentersList } from '@/pages/agents/components/avatars/PresentersList';
import { UpgradeModal } from '@/pages/agents/components/avatars/UpgradeModal';
import { useGetAllVoicesFlat, useResetAgentsSelectedPresenter, useUpdateAgentLoader } from '@/pages/agents/hooks';
import { AvatarFilter, AvatarFilterDeprecated, avatarToFilter } from '@/pages/avatar';
import { useAllAvatars } from '@/pages/avatar/hooks/useAllAvatars';
import { useAnalytics } from '@/services/analytics';
import { DidUser, useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { getDefaultVoice } from '@/services/presenters/getDefaultVoice';
import { AgentDefaultLanguage } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import { isNonPremiumUser } from '@/utils/utils';
import { AgentCreateTabType, IPresenter, isDvpPresenter, Presenter, presenterTypeMap, PresenterUsecase, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useAgentsPresenters } from '../../hooks';
import { onApplyBackgroundSuccess, onUpdateBackground } from './utils';

const PresenterListWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding-top: 8px;

    @media (min-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const AgentAppearanceContainer = styled.div<{ hideOverflowY?: boolean }>`
    ${({ hideOverflowY }) => !hideOverflowY && 'overflow-y: auto;'}
`;

export const needsToUpgradeForThisPresenter = (
    presenter: Presenter | undefined,
    user: DidUser | null | undefined,
    isAnonymous: boolean | undefined
) => {
    if (!presenter) return false;

    const userPlan = user?.planGroup ?? user?.productName;
    const disabledSPPlus = isAnonymous || (isNonPremiumUser(userPlan) && !user?.isTrial);

    return disabledSPPlus && presenter.type === VideoType.CLIP;
};

interface Props {
    presenter?: Presenter;
    hideOverflowY?: boolean;
    agentId?: string;
}

export const AgentAppearance = observer(({ agentId, presenter, hideOverflowY }: Props) => {
    const mixpanel = useAnalytics();
    const { user, isAnonymous } = useAuth();
    const agentEditorStore = useAgentEditorStore();

    const [isBackgroundsModalOpen, setIsBackgroundsModalOpen] = useState(false);
    const [presenterToDelete, setPresenterToDelete] = useState<IPresenter>();
    const [initialPresenterFilterHasBeenSet, setInitialPresenterFilterHasBeenSet] = useState(false);

    const newAvatarsFiltersEnabled = useFlag('new_avatars_filters', false);
    const [filter, setFilter] = useState<AvatarFilter | AvatarFilterDeprecated>(
        newAvatarsFiltersEnabled ? AvatarFilter.All : AvatarFilterDeprecated.PremiumPlus
    );

    const { mutateAsync: applyBackground } = useApplyBackground(onApplyBackgroundSuccess, presenter?.type);
    const { mutateAsync: applyBackgroundColor } = useApplyBackgroundColor(onApplyBackgroundSuccess, presenter?.type);
    const resetSelectedPresenter = useResetAgentsSelectedPresenter();
    const voices = useGetAllVoicesFlat();
    const { removeBackground } = useBackgroundRemoval();
    const { showAgentLoader, hideAgentLoader } = useUpdateAgentLoader();

    const isAgentV2 = agentId ? !!agentId.startsWith('v2_') : true;
    const { allAvatars } = useAllAvatars(isAgentV2 ? PresenterUsecase.AgentV2 : PresenterUsecase.Agent);
    const presentersToShow = useAgentsPresenters({ isAgentV2, filter });

    const migratableAvatars: string[] = useFlag('migrated_avatars', []);

    useEffect(() => {
        if (initialPresenterFilterHasBeenSet) return;

        const selectedAvatarFilter = avatarToFilter(presenter as any, user);
        if (selectedAvatarFilter) {
            setFilter(selectedAvatarFilter);
            setInitialPresenterFilterHasBeenSet(true);
        }
    }, [setFilter, presenter, user, initialPresenterFilterHasBeenSet]);

    const onCloseBackgroundsModal = () => {
        setIsBackgroundsModalOpen(false);
    };

    const handleUpdateBackground = async (background: string) => {
        onCloseBackgroundsModal();

        mixpanel?.track('agent-update-background', {
            event: 'click',
            agentType: 'gallery',
            presenterType: presenter?.type,
        });

        await onUpdateBackground({
            presenter,
            user,
            background,
            createImagesDns,
            createImagesBucket,
            removeBackground,
            applyBackground,
            applyBackgroundColor,
            showAgentLoader,
            hideAgentLoader,
        });
    };

    const setPresenterDefaultVoice = (presenter: Presenter) => {
        if (presenter && presenter.voice) {
            const presenterVoices = voices.filter(v => v.id === presenter.voice?.voice_id);
            if (presenterVoices && presenterVoices.length > 0) {
                const voice = presenterVoices.find(v => v.language === AgentDefaultLanguage) || presenterVoices[0];
                runInAction(() => {
                    agentEditorStore.setTtsSelectedVoice(voice, false);
                    agentEditorStore.setTtsSelectedLanguage(voice.language);
                });
            }
        }
    };

    const onPresenterClick = useCallback(
        (presenter: Presenter) => {
            if (needsToUpgradeForThisPresenter(presenter, user, isAnonymous)) {
                agentEditorStore.setShowUpgradeModal(true);
                return;
            }

            runInAction(() => {
                agentEditorStore.setSelectedPresenter(presenter.id);
                agentEditorStore.setSelectedPresenterType(presenter?.presenterType || agentEditorStore.selectedPresenterType);
                agentEditorStore.resetPresenterBackgroundSourceUrl();
                agentEditorStore.resetPresenterBackgroundThumbnailUrl();

                const defaultVoice = getDefaultVoice(presenter, voices);
                agentEditorStore.setTtsSelectedVoice(defaultVoice, false);
            });

            mixpanel?.track('agent-editor', {
                event: 'click',
                page: AgentCreateTabType.Appearance,
                from: presenter.isDefault ? presenterTypeMap[presenter.type] || 'Unknown' : 'Upload',
                isV2: isDvpPresenter(presenter),
            });
        },
        [
            user,
            isAnonymous,
            agentEditorStore.setShowUpgradeModal,
            agentEditorStore.setSelectedPresenter,
            agentEditorStore.setSelectedPresenterType,
        ]
    );

    useEffect(() => {
        if (agentEditorStore.isVoiceManuallySelected) {
            return;
        }

        const presenter = allAvatars.find(p => p.id === agentEditorStore.selectedPresenter);

        if (presenter) {
            setPresenterDefaultVoice(presenter);
        }
    }, [agentEditorStore.isVoiceManuallySelected, agentEditorStore.selectedPresenter, allAvatars]);

    function hideTab(tab: AvatarFilterDeprecated): boolean {
        if (isAgentV2 && !!migratableAvatars.length) {
            if (tab === AvatarFilterDeprecated.Premium) {
                return true;
            }
        }

        return false;
    }

    return (
        <AgentAppearanceContainer hideOverflowY={hideOverflowY}>
            <AvatarTabs filter={filter} setFilter={setFilter} hideTab={hideTab} />
            <PresenterListWrapper>
                <AddAvatarCard />
                <PresentersList
                    agentId={agentId}
                    presenters={presentersToShow}
                    onClick={onPresenterClick}
                    onRemove={setPresenterToDelete}
                    selectedPresenter={presenter as Presenter | undefined}
                    setIsBackgroundsModalOpen={setIsBackgroundsModalOpen}
                />
            </PresenterListWrapper>

            <UpgradeModal open={agentEditorStore.showUpgradeModal} setOpen={agentEditorStore.setShowUpgradeModal} />
            <DeleteAvatarModal
                presenter={presenterToDelete}
                onDelete={() => {
                    if (presenterToDelete?.id && presenterToDelete.id === presenter?.id) {
                        resetSelectedPresenter({ type: agentEditorStore.selectedPresenterType, exclude: presenterToDelete.id });
                    }
                }}
                onClose={() => setPresenterToDelete(undefined)}
            />
            <BackgroundsModal
                isOpen={isBackgroundsModalOpen}
                title="Select a background"
                onClose={onCloseBackgroundsModal}
                onUpdate={handleUpdateBackground}
            />
        </AgentAppearanceContainer>
    );
});
